import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Car,
  QrCode,
  Receipt,
  ScanLine,
  PauseCircle,
  Settings,
  Shield,
  LogOut,
  Check,
  Building2,
  Sticker,
  Upload,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, id: "nav-overview" },
  { to: "/dashboard/tenants", label: "Tenants", icon: Users, id: "nav-tenants" },
  { to: "/dashboard/vehicles", label: "Vehicles", icon: Car, id: "nav-vehicles" },
  { to: "/dashboard/bulk-import", label: "Bulk import", icon: Upload, id: "nav-bulk" },
  { to: "/dashboard/qr-codes", label: "QR Codes", icon: QrCode, id: "nav-qr" },
  { to: "/dashboard/billing", label: "Billing", icon: Receipt, id: "nav-billing" },
  { to: "/dashboard/stickers", label: "Stickers", icon: Sticker, id: "nav-stickers" },
  { to: "/dashboard/scan-logs", label: "Scan Logs", icon: ScanLine, id: "nav-scanlogs" },
  { to: "/dashboard/suspended", label: "Suspended", icon: PauseCircle, id: "nav-suspended" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout, properties: rawProps, activeProperty, switchProperty } = useAuth();
  const properties = rawProps || []; // null = still loading — render as empty
  const navigate = useNavigate();

  const isAdmin = user?.role === "super_admin";

  return (
    <div className="min-h-screen flex bg-[#FAFAFA] text-[#09090B]">
      <aside
        data-testid="sidebar"
        className="hidden md:flex w-64 flex-col border-r border-[#E4E4E7] bg-white sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b border-[#E4E4E7]">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-left"
            data-testid="brand-home"
          >
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </button>
        </div>

        {/* property switcher */}
        <div className="px-4 py-4 border-b border-[#E4E4E7]">
          <div className="mono-tiny text-[#71717A] mb-2">Active property</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between h-11 bg-white border-[#E4E4E7]"
                data-testid="property-switcher"
              >
                <span className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
                  <span className="truncate text-sm font-medium">
                    {activeProperty?.property_name || "No property"}
                  </span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel>Your properties</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {properties.length === 0 && (
                <DropdownMenuItem disabled>No properties yet</DropdownMenuItem>
              )}
              {properties.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => switchProperty(p.id)}
                  data-testid={`switch-property-${p.id}`}
                >
                  <span className="flex items-center gap-2 flex-1">
                    <span className="truncate">{p.property_name}</span>
                  </span>
                  {activeProperty?.id === p.id && <Check className="w-4 h-4" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/onboarding?add=1")} data-testid="add-property">
                + Add new property
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard"}
              data-testid={item.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0F8F4F] text-white"
                    : "text-[#09090B] hover:bg-[#F4F4F5]"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}

          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#09090B] text-white"
                    : "text-[#09090B] hover:bg-[#F4F4F5]"
                }`
              }
            >
              <Shield className="w-4 h-4" strokeWidth={1.5} />
              Super Admin
            </NavLink>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0F8F4F]/10 flex items-center justify-center text-[#0F8F4F] font-semibold">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="mono-tiny text-[#71717A]">{user?.role?.replace("_", " ")}</div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* mobile topbar */}
        <div className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E4E4E7] px-4 py-3 flex items-center justify-between">
          <button
            className="flex items-center gap-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-brand-home"
          >
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-9 w-auto select-none"
              draggable={false}
            />
          </button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>

        {/* mobile property switcher + nav */}
        <div className="md:hidden bg-white border-b border-[#E4E4E7] px-4 py-3 space-y-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between h-11"
                data-testid="mobile-property-switcher"
              >
                <span className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
                  <span className="truncate text-sm font-medium">
                    {activeProperty?.property_name || "No property"}
                  </span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[90vw]">
              {properties.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => switchProperty(p.id)}
                  data-testid={`mobile-switch-property-${p.id}`}
                >
                  {p.property_name}
                  {activeProperty?.id === p.id && <Check className="ml-auto w-4 h-4" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/onboarding?add=1")} data-testid="mobile-add-property">
                + Add new property
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/dashboard"}
                data-testid={`mobile-${item.id}`}
                className={({ isActive }) =>
                  `shrink-0 px-3 py-2 rounded-md text-xs font-medium border ${
                    isActive
                      ? "bg-[#0F8F4F] text-white border-[#0F8F4F]"
                      : "bg-white text-[#09090B] border-[#E4E4E7]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink
                to="/admin"
                data-testid="mobile-nav-admin"
                className={({ isActive }) =>
                  `shrink-0 px-3 py-2 rounded-md text-xs font-medium border ${
                    isActive
                      ? "bg-[#09090B] text-white border-[#09090B]"
                      : "bg-white text-[#09090B] border-[#E4E4E7]"
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </div>
        </div>

        <main className="flex-1 p-4 md:p-8 max-w-[1400px] mx-auto w-full">{children}</main>
      </div>
    </div>
  );
}
